import { useQueryClient } from '@tanstack/react-query';
import { SymbolApiModel } from '../../../../types/models/symbol.model';
import { CreateSymbolApiRequest } from '../../../../types/requests/symbols/create-symbol.request';
import { DeleteSymbolApiRequest } from '../../../../types/requests/symbols/delete-symbol.request';
import { GetSymbolApiRequest } from '../../../../types/requests/symbols/get-symbol.request';
import { SearchSymbolsApiRequest } from '../../../../types/requests/symbols/search-symbols.request';
import { UpdateSymbolApiRequest } from '../../../../types/requests/symbols/update-symbol.request';
import { createLibrary, createSymbol, deleteSymbol, getSymbol, searchLibraries, searchSymbols, updateSymbol } from '../../api/symbols.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { exportSymbols, importSymbols } from '../../api/sync.api';
import { ImportSymbolsApiRequest } from '../../../../types/requests/sync/import-symbols.request';
import { ExportSymbolsApiRequest } from '../../../../types/requests/sync/export-symbols.request';
import { SearchLibrariesApiRequest } from '../../../../types/requests/symbols/libraries/search-libraries.request';
import { CreateLibraryApiRequest } from '../../../../types/requests/symbols/libraries/create-libary.request';

const baseQueryKey = 'symbol';
const searchQueryKey = 'search';

export const useCreateSymbol = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateSymbolApiRequest) => (await createSymbol(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, symbol: data }] as const;
};

export const useUpdateSymbol = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateSymbolApiRequest) => (await updateSymbol(params)).data,
    onSuccess: (symbol) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetSymbolApiRequest)?.symbolId === symbol.id),
      }),
  });
  return [request, { ...result, symbol: data }] as const;
};

export const useGetSymbol = <T extends SymbolApiModel>(params: GetSymbolApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getSymbol<T>(params)).data,
    enabled: Boolean(params.symbolId && !Number.isNaN(params.symbolId)),
  });
  return { ...result, symbol: data } as const;
};

export const useSearchSymbols = (params: SearchSymbolsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchSymbols({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteSymbol = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteSymbolApiRequest) => (await deleteSymbol(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useCreateLibrary = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateLibraryApiRequest) => (await createLibrary(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === 'library' && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, library: data }] as const;
};

export const useSearchLibraries = (params: SearchLibrariesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: ['library', searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchLibraries({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useImportSymbols = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: ImportSymbolsApiRequest) => (await importSymbols(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey[0] === baseQueryKey }),
  });
  return [request, { response: data, ...result }] as const;
};

export const useExportSymbols = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: ExportSymbolsApiRequest) => (await exportSymbols(params)).data,
  });
  return [request, result] as const;
};
